import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CheckCircle, XCircle } from "lucide-react";

export default function AuthCallback() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing verification...');
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');

        if (error) {
          setStatus('error');
          setMessage(`Verification failed: ${error}`);
          return;
        }

        if (!code) {
          setStatus('error');
          setMessage('No verification code received');
          return;
        }

        // Get current session
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setStatus('error');
          setMessage('Not authenticated');
          return;
        }

        // Call our hosted verify function to complete verification
        const { data, error: verifyError } = await supabase.functions.invoke('mojoauth-hosted-verify', {
          headers: { Authorization: `Bearer ${session.access_token}` },
          body: {
            type: 'phone',
            action: 'callback',
            token: code
          }
        });

        if (verifyError) {
          console.error('Verification error:', verifyError);
          setStatus('error');
          setMessage(verifyError.message || 'Verification failed');
          return;
        }

        setStatus('success');
        setMessage('Phone verification completed successfully!');
        
        toast({
          title: "Phone Verified",
          description: "Your phone number has been verified successfully.",
        });

        // Close popup if this is in a popup window
        if (window.opener) {
          // Notify parent window
          window.opener.postMessage({ type: 'PHONE_VERIFIED' }, window.location.origin);
          window.close();
        } else {
          // Redirect to main page after a short delay
          setTimeout(() => {
            navigate('/');
          }, 2000);
        }

      } catch (error: any) {
        console.error('Callback handling error:', error);
        setStatus('error');
        setMessage(error.message || 'An unexpected error occurred');
      }
    };

    handleCallback();
  }, [navigate, toast]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            {status === 'loading' && (
              <Loader2 className="w-12 h-12 mx-auto text-blue-600 animate-spin" />
            )}
            {status === 'success' && (
              <CheckCircle className="w-12 h-12 mx-auto text-green-600" />
            )}
            {status === 'error' && (
              <XCircle className="w-12 h-12 mx-auto text-red-600" />
            )}
          </div>
          
          <h1 className="text-2xl font-bold mb-4 text-gray-900">
            {status === 'loading' && 'Processing Verification'}
            {status === 'success' && 'Verification Complete'}
            {status === 'error' && 'Verification Failed'}
          </h1>
          
          <p className="text-gray-600 mb-6">{message}</p>
          
          {status === 'success' && window.opener && (
            <p className="text-sm text-gray-500">
              This window will close automatically...
            </p>
          )}
          
          {status === 'error' && (
            <button
              onClick={() => window.close()}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}